import { useState, useEffect, useRef, useCallback } from 'react';
import ImageLightbox from '../../components/ImageLightbox';
import { useAuth } from '../../App';
import { AdminLayout } from '../../components/AdminLayout';
import {
  MessageSquare,
  Send,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Check,
  XCircle,
  HelpCircle,
  Clock,
  Filter,
  X,
  RefreshCw,
  Bug,
  Globe,
  Monitor,
  AlertTriangle,
  Info,
} from 'lucide-react';
import {
  getAllFeedbacks,
  updateFeedbackStatus,
  addFeedbackComment,
  getFeedbackImageUrl,
  type Feedback,
  type FeedbackComment,
  type FeedbackCategory,
  type FeedbackStatus,
} from '../../api/client';

const CATEGORY_OPTIONS: { value: FeedbackCategory; label: string }[] = [
  { value: 'BUG', label: 'Fehler' },
  { value: 'FEATURE', label: 'Wunsch' },
  { value: 'OTHER', label: 'Sonstiges' },
];

const STATUS_OPTIONS: { value: FeedbackStatus; label: string }[] = [
  { value: 'OPEN', label: 'Offen' },
  { value: 'RUCKFRAGE', label: 'Rueckfrage' },
  { value: 'DONE', label: 'Erledigt' },
  { value: 'REJECTED', label: 'Abgelehnt' },
];

const STATUS_CONFIG: Record<FeedbackStatus, { label: string; icon: typeof Check; color: string; bgColor: string }> = {
  OPEN: { label: 'Offen', icon: Clock, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  RUCKFRAGE: { label: 'Rueckfrage', icon: HelpCircle, color: 'text-amber-600', bgColor: 'bg-amber-50' },
  DONE: { label: 'Erledigt', icon: Check, color: 'text-green-600', bgColor: 'bg-green-50' },
  REJECTED: { label: 'Abgelehnt', icon: XCircle, color: 'text-slate-500', bgColor: 'bg-slate-100' },
};

export default function FeedbackPage() {
  const { user } = useAuth();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<FeedbackStatus | ''>('');
  const [filterCategory, setFilterCategory] = useState<FeedbackCategory | ''>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Comment state
  const [commentText, setCommentText] = useState<Record<string, string>>({});
  const [commentImage, setCommentImage] = useState<Record<string, File | null>>({});
  const [commentSubmitting, setCommentSubmitting] = useState<Record<string, boolean>>({});

  const isSystem = user?.role === 'SYSTEM';

  const loadFeedbacks = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filterStatus) params.status = filterStatus;
      if (filterCategory) params.category = filterCategory;
      const data = await getAllFeedbacks(params);
      setFeedbacks(data);
    } catch (e) {
      console.error('Failed to load feedbacks:', e);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCategory]);

  useEffect(() => {
    loadFeedbacks();
  }, [loadFeedbacks]);

  const handleStatusChange = async (feedbackId: string, newStatus: FeedbackStatus) => {
    try {
      await updateFeedbackStatus(feedbackId, newStatus);
      loadFeedbacks();
    } catch (e) {
      console.error('Status update failed:', e);
    }
  };

  const handleAddComment = async (feedbackId: string) => {
    const message = commentText[feedbackId]?.trim();
    const image = commentImage[feedbackId];
    if (!message && !image) return;

    setCommentSubmitting((prev) => ({ ...prev, [feedbackId]: true }));
    try {
      await addFeedbackComment(feedbackId, { message, image: image || undefined });
      setCommentText((prev) => ({ ...prev, [feedbackId]: '' }));
      setCommentImage((prev) => ({ ...prev, [feedbackId]: null }));
      loadFeedbacks();
    } catch (e) {
      console.error('Comment failed:', e);
    } finally {
      setCommentSubmitting((prev) => ({ ...prev, [feedbackId]: false }));
    }
  };

  const handlePaste = (feedbackId: string, e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) {
          setCommentImage((prev) => ({ ...prev, [feedbackId]: file }));
          e.preventDefault();
          break;
        }
      }
    }
  };

  // Stats
  const openCount = feedbacks.filter((f) => f.status === 'OPEN').length;
  const ruckfrageCount = feedbacks.filter((f) => f.status === 'RUCKFRAGE').length;

  return (
    <AdminLayout>
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Meldungen</h1>
          <p className="text-sm text-slate-500 mt-1">
            {feedbacks.length} Meldungen
            {openCount > 0 && <span className="ml-2 text-blue-600">{openCount} offen</span>}
            {ruckfrageCount > 0 && <span className="ml-2 text-amber-600">{ruckfrageCount} Rueckfrage</span>}
          </p>
        </div>
        <button
          onClick={loadFeedbacks}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as FeedbackStatus | '')}
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="">Alle Status</option>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value as FeedbackCategory | '')}
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="">Alle Kategorien</option>
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Feedback List */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Laden...</div>
      ) : feedbacks.length === 0 ? (
        <div className="text-center py-12 text-slate-400">Keine Meldungen</div>
      ) : (
        <div className="space-y-3">
          {feedbacks.map((fb) => (
            <FeedbackCard
              key={fb.id}
              feedback={fb}
              isExpanded={expandedId === fb.id}
              onToggle={() => setExpandedId(expandedId === fb.id ? null : fb.id)}
              isSystem={isSystem}
              currentUserId={user?.userId || ''}
              onStatusChange={handleStatusChange}
              commentText={commentText[fb.id] || ''}
              onCommentTextChange={(text) => setCommentText((prev) => ({ ...prev, [fb.id]: text }))}
              commentImage={commentImage[fb.id] || null}
              onCommentImageChange={(file) => setCommentImage((prev) => ({ ...prev, [fb.id]: file }))}
              onPaste={(e) => handlePaste(fb.id, e)}
              onSubmitComment={() => handleAddComment(fb.id)}
              isSubmitting={commentSubmitting[fb.id] || false}
            />
          ))}
        </div>
      )}
    </div>
    </AdminLayout>
  );
}

// ============================================
// FeedbackCard Component
// ============================================

function FeedbackCard({
  feedback,
  isExpanded,
  onToggle,
  isSystem,
  currentUserId,
  onStatusChange,
  commentText,
  onCommentTextChange,
  commentImage,
  onCommentImageChange,
  onPaste,
  onSubmitComment,
  isSubmitting,
}: {
  feedback: Feedback;
  isExpanded: boolean;
  onToggle: () => void;
  isSystem: boolean;
  currentUserId: string;
  onStatusChange: (feedbackId: string, status: FeedbackStatus) => void;
  commentText: string;
  onCommentTextChange: (text: string) => void;
  commentImage: File | null;
  onCommentImageChange: (file: File | null) => void;
  onPaste: (e: React.ClipboardEvent) => void;
  onSubmitComment: () => void;
  isSubmitting: boolean;
}) {
  const statusCfg = STATUS_CONFIG[feedback.status];
  const StatusIcon = statusCfg.icon;
  const categoryLabel = CATEGORY_OPTIONS.find((o) => o.value === feedback.category)?.label || feedback.category;
  const date = new Date(feedback.createdAt);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const submitterName = feedback.submittedBy?.displayName || 'Unbekannt';
  const stationName = feedback.station?.name || feedback.station?.type || null;
  const messageCount = feedback.comments.filter((c) => c.type === 'MESSAGE').length;
  const debugComments = feedback.comments.filter((c) => c.type === 'DEBUG_DATA');

  // Beteiligte: Ersteller + Kommentar-Autoren (unique)
  const participants = (() => {
    const names = new Map<string, string>();
    if (feedback.submittedBy) names.set(feedback.submittedBy.id, feedback.submittedBy.displayName);
    for (const c of feedback.comments) {
      if (c.author && !names.has(c.author.id)) names.set(c.author.id, c.author.displayName);
    }
    return Array.from(names.values());
  })();

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
      {/* Header */}
      <button onClick={onToggle} className="w-full text-left px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-slate-400">#{feedback.number}</span>
              <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${statusCfg.color}`}>
                <StatusIcon className="w-3 h-3" />
                {statusCfg.label}
              </div>
              <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{categoryLabel}</span>
              {debugComments.length > 0 && (
                <span className="flex items-center gap-0.5 text-[11px] text-orange-500" title="Debug-Snapshots vorhanden">
                  <Bug className="w-3 h-3" />
                  {debugComments.length}
                </span>
              )}
            </div>
            <p className="text-sm font-semibold text-slate-800 line-clamp-2">{feedback.title}</p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-xs text-slate-400">
              <span className="font-medium text-slate-500">{participants.join(', ')}</span>
              {stationName && (
                <>
                  <span className="text-slate-300">&middot;</span>
                  <span>Station: {stationName}</span>
                </>
              )}
              <span className="text-slate-300">&middot;</span>
              <span>{date.toLocaleDateString('de-DE')} {date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
              {messageCount > 0 && (
                <>
                  <span className="text-slate-300">&middot;</span>
                  <span className="flex items-center gap-0.5">
                    <MessageSquare className="w-3 h-3" />
                    {messageCount} Nachricht{messageCount !== 1 ? 'en' : ''}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="mt-1 shrink-0">
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t px-4 py-3 space-y-3">
          {/* Header mit Status-Dropdown */}
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-400">
              {feedback.submittedBy?.displayName || 'Unbekannt'}
              {stationName ? ` · ${stationName}` : ''}
              {feedback.pageUrl ? ` · ${feedback.pageUrl}` : ''}
            </div>
            {isSystem ? (
              <select
                value={feedback.status}
                onChange={(e) => onStatusChange(feedback.id, e.target.value as FeedbackStatus)}
                className={`text-sm border rounded-md px-3 py-1 bg-white cursor-pointer hover:bg-gray-50 font-medium ${statusCfg.color}`}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <div className={`text-xs font-medium px-2 py-0.5 rounded ${statusCfg.color} ${statusCfg.bgColor}`}>
                {statusCfg.label}
              </div>
            )}
          </div>

          {/* --- CHAT CONTAINER START --- */}
          <div className="flex flex-col gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg h-[600px] overflow-y-auto">
            {feedback.comments.map((comment) => {
              // 1. SYSTEM EVENTS
              if (comment.type === 'SYSTEM_EVENT') {
                return (
                  <div key={comment.id} className="flex justify-center my-2">
                    <span className="text-xs text-gray-400 font-medium bg-gray-100 px-3 py-1 rounded-full">
                      — {comment.message} —
                    </span>
                  </div>
                );
              }

              // 2. DEBUG DATA (Rot und Auffaellig)
              if (comment.type === 'DEBUG_DATA') {
                let debugContent = 'Fehlerhafte Daten';
                try {
                  debugContent = JSON.stringify(JSON.parse(comment.metadata || '{}'), null, 2);
                } catch (e) { /* ignore */ }

                return (
                  <details key={comment.id} className="group border border-red-200 bg-red-50 rounded-md mb-4 overflow-hidden">
                    <summary className="cursor-pointer p-2 text-xs font-bold text-red-700 hover:bg-red-100 flex items-center gap-2">
                      <Bug className="w-4 h-4" />
                      <span>TECH-SNAPSHOT / DEBUG DATEN</span>
                      <span className="font-normal text-red-500">(Klicken zum Oeffnen)</span>
                    </summary>
                    <pre className="p-3 text-[10px] leading-tight overflow-x-auto bg-white border-t border-red-100 text-slate-700 max-h-48 overflow-y-auto">
                      {debugContent}
                    </pre>
                  </details>
                );
              }

              // 3. MESSAGES (Chat Style)
              const isMe = comment.authorId === currentUserId;

              return (
                <div
                  key={comment.id}
                  className={`flex w-full mb-4 ${isMe ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] p-4 shadow-sm relative text-sm ${
                      isMe
                        ? 'bg-green-50 border border-green-200 rounded-2xl rounded-tr-sm'
                        : 'bg-sky-50 border border-sky-200 rounded-2xl rounded-tl-sm'
                    }`}
                  >
                    {/* Header: Name und Zeit */}
                    <div className="flex justify-between items-baseline gap-4 mb-2 border-b border-black/5 pb-1">
                      <span className={`font-semibold ${isMe ? 'text-green-800' : 'text-sky-800'}`}>
                        {comment.author?.displayName || 'Unbekannt'}
                      </span>
                      <span className="text-[10px] opacity-60">
                        {new Date(comment.createdAt).toLocaleString('de-DE')}
                      </span>
                    </div>

                    {/* Nachricht */}
                    {comment.message && (
                      <div className="whitespace-pre-wrap break-words text-gray-800 leading-relaxed">{comment.message}</div>
                    )}

                    {/* Bild */}
                    {comment.imagePath && (
                      <div className="mt-3">
                        <img
                          src={getFeedbackImageUrl(comment.imagePath)}
                          alt="Attachment"
                          className="rounded border border-black/5 max-h-64 object-contain bg-white cursor-pointer hover:opacity-90"
                          onClick={() => setLightboxSrc(getFeedbackImageUrl(comment.imagePath!))}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {/* --- CHAT CONTAINER END --- */}

          {/* Antwort schreiben */}
          <div className="space-y-2">
            <div className="relative">
              <textarea
                value={commentText}
                onChange={(e) => onCommentTextChange(e.target.value)}
                onPaste={onPaste}
                placeholder="Antwort schreiben..."
                rows={2}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-slate-300 focus:outline-none pr-10"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute right-2 bottom-2 text-slate-400 hover:text-slate-600"
                title="Bild hinzufuegen"
              >
                <ImageIcon className="w-4 h-4" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  onCommentImageChange(file);
                  e.target.value = '';
                }}
              />
            </div>

            {commentImage && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <ImageIcon className="w-3 h-3" />
                <span className="truncate">{commentImage.name || 'Screenshot'}</span>
                <button
                  onClick={() => onCommentImageChange(null)}
                  className="text-red-400 hover:text-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            <button
              onClick={onSubmitComment}
              disabled={isSubmitting || (!commentText.trim() && !commentImage)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-white rounded-lg text-xs font-medium hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-3 h-3" />
              {isSubmitting ? 'Senden...' : 'Antwort senden'}
            </button>
          </div>
        </div>
      )}

      {lightboxSrc && (
        <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      )}
    </div>
  );
}

// ============================================
// Debug Data Panel
// ============================================

interface DebugSnapshot {
  consoleLogs?: { type: string; args: string[]; timestamp: number }[];
  networkErrors?: { method: string; url: string; status: number | null; error?: string; timestamp: number }[];
  systemInfo?: { userAgent?: string; viewport?: string; screen?: string; language?: string; url?: string; referrer?: string };
  timestamp?: string;
  uptimeMs?: number;
}

function DebugDataPanel({ comment }: { comment: FeedbackComment }) {
  let data: DebugSnapshot | null = null;
  try {
    data = comment.metadata ? JSON.parse(comment.metadata) : null;
  } catch {
    return null;
  }
  if (!data) return null;

  const logTypeColors: Record<string, string> = {
    error: 'text-red-600',
    warn: 'text-amber-600',
    log: 'text-slate-500',
  };

  const logTypeIcons: Record<string, typeof AlertTriangle> = {
    error: XCircle,
    warn: AlertTriangle,
    log: Info,
  };

  return (
    <details className="border border-orange-200 bg-orange-50/50 rounded-lg overflow-hidden">
      <summary className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-orange-50 transition-colors text-sm">
        <Bug className="w-4 h-4 text-orange-500 shrink-0" />
        <span className="font-medium text-orange-700">Tech-Snapshot</span>
        <span className="text-[11px] text-orange-400 ml-auto">
          {new Date(comment.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </summary>
      <div className="border-t border-orange-200 px-3 py-2.5 space-y-3">
        {/* System Info */}
        {data.systemInfo && (
          <div className="space-y-1">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">System</h4>
            <div className="grid grid-cols-2 gap-1 text-xs">
              {data.systemInfo.url && (
                <div className="col-span-2 flex items-center gap-1.5 text-slate-600">
                  <Globe className="w-3 h-3 text-slate-400" />
                  <span className="font-mono truncate">{data.systemInfo.url}</span>
                </div>
              )}
              {data.systemInfo.viewport && (
                <div className="flex items-center gap-1.5 text-slate-500">
                  <Monitor className="w-3 h-3 text-slate-400" />
                  Viewport: {data.systemInfo.viewport}
                </div>
              )}
              {data.systemInfo.screen && (
                <div className="text-slate-500">Screen: {data.systemInfo.screen}</div>
              )}
              {data.systemInfo.userAgent && (
                <div className="col-span-2 text-slate-400 truncate text-[11px] font-mono">{data.systemInfo.userAgent}</div>
              )}
            </div>
          </div>
        )}

        {/* Console Logs */}
        {data.consoleLogs && data.consoleLogs.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">
              Console ({data.consoleLogs.length})
            </h4>
            <div className="bg-slate-900 rounded-lg p-2 max-h-[200px] overflow-y-auto">
              {data.consoleLogs.map((log, i) => {
                const LogIcon = logTypeIcons[log.type] || Info;
                return (
                  <div key={i} className={`flex items-start gap-1.5 text-[11px] font-mono leading-relaxed ${logTypeColors[log.type] || 'text-slate-400'}`}>
                    <LogIcon className="w-3 h-3 shrink-0 mt-0.5" />
                    <span className="break-all">{log.args.join(' ')}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Network Errors */}
        {data.networkErrors && data.networkErrors.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-1">
              Network Errors ({data.networkErrors.length})
            </h4>
            <div className="bg-red-950 rounded-lg p-2 max-h-[150px] overflow-y-auto">
              {data.networkErrors.map((err, i) => (
                <div key={i} className="text-[11px] font-mono text-red-300 leading-relaxed">
                  <span className="text-red-400 font-bold">{err.method}</span>{' '}
                  <span className="text-red-200">{err.url}</span>{' '}
                  <span className="text-red-500">{err.status ?? 'ERR'}</span>
                  {err.error && <span className="text-red-400 ml-1">({err.error})</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Uptime */}
        {data.uptimeMs !== undefined && (
          <p className="text-[11px] text-slate-400">
            Uptime: {Math.round(data.uptimeMs / 1000)}s
            {data.timestamp && <span className="ml-2">Snapshot: {new Date(data.timestamp).toLocaleTimeString('de-DE')}</span>}
          </p>
        )}
      </div>
    </details>
  );
}
